import { z } from "zod";
import { UuidSchema } from "./common.js";

// Spec 078 — Biblioteca de modelos 3D (D-1: biblioteca por organização, não upload por máquina).
// A CALIBRAÇÃO pertence ao modelo: escala, altura de apoio e os pontos de entrada/saída do
// produto, em coordenadas LOCAIS do modelo — mover ou girar a máquina transforma os pontos junto.

const Vec3Schema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  z: z.number().finite(),
});
export type Vec3 = z.infer<typeof Vec3Schema>;

export const Model3dCalibrationSchema = z.object({
  /** Multiplicador uniforme (> 0). Padrão 1. */
  scale: z.number().positive().max(1000),
  /** Metros somados em Y para o modelo apoiar no chão (padrão −minY da bounding box). */
  groundOffset: z.number().finite(),
  /** Onde o produto ENTRA, no espaço local do modelo (escala 1). */
  entryPoint: Vec3Schema,
  /** Onde o produto SAI, no espaço local do modelo (escala 1). */
  exitPoint: Vec3Schema,
  /** Só informativo, preenchido pelo estúdio (bounding box do arquivo). */
  boundsHint: z
    .object({
      minY: z.number().finite(),
      maxY: z.number().finite(),
      radius: z.number().finite().nonnegative(),
    })
    .optional(),
});
export type Model3dCalibration = z.infer<typeof Model3dCalibrationSchema>;

/** Calibração de nascimento: a cena já desenha esteiras antes da calibração fina. */
export const MODEL3D_DEFAULT_CALIBRATION: Model3dCalibration = {
  scale: 1,
  groundOffset: 0,
  entryPoint: { x: -1, y: 0.5, z: 0 },
  exitPoint: { x: 1, y: 0.5, z: 0 },
};

export const Model3dSummarySchema = z.object({
  id: UuidSchema,
  name: z.string(),
  fileHash: z.string().length(64),
  fileSize: z.number().int().nonnegative(),
  /** URL RELATIVA do arquivo, já com `?v=<hash>` (cache imutável por versão). */
  fileUrl: z.string(),
  calibration: Model3dCalibrationSchema,
  machineCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Model3dSummary = z.infer<typeof Model3dSummarySchema>;

export const Model3dDetailSchema = Model3dSummarySchema.extend({
  machines: z.array(
    z.object({
      id: UuidSchema,
      code: z.string(),
      name: z.string(),
      lineId: UuidSchema,
      lineName: z.string(),
    })
  ),
});
export type Model3dDetail = z.infer<typeof Model3dDetailSchema>;

export const Model3dNameSchema = z.string().trim().min(1).max(80);

/** Campos de formulário do multipart (o arquivo vai no campo `file`). */
export const CreateModel3dFieldsSchema = z.object({ name: Model3dNameSchema });
export type CreateModel3dFields = z.infer<typeof CreateModel3dFieldsSchema>;

export const UpdateModel3dRequestSchema = z
  .object({
    name: Model3dNameSchema.optional(),
    calibration: Model3dCalibrationSchema.optional(),
  })
  .strict()
  .refine((b) => b.name !== undefined || b.calibration !== undefined, {
    message: "Informe o nome ou a calibração.",
  });
export type UpdateModel3dRequest = z.infer<typeof UpdateModel3dRequestSchema>;

/** Exclusão exige digitar o nome do modelo (ConfirmDialog — regra do design system). */
export const DeleteModel3dRequestSchema = z
  .object({ confirmationName: z.string().min(1) })
  .strict();
export type DeleteModel3dRequest = z.infer<typeof DeleteModel3dRequestSchema>;

/** Query da rota de arquivo: `?v=` é só cache-busting, nunca seleciona versão. */
export const Model3dFileQuerySchema = z.object({ v: z.string().max(64).optional() });

/** Motivo de recusa no upload (FR-002): a mensagem diz o critério e o excedente. */
export const Model3dRejectReasonSchema = z.enum([
  "extension",
  "magic",
  "version",
  "length",
  "size",
]);
export type Model3dRejectReason = z.infer<typeof Model3dRejectReasonSchema>;

// Envelopes de resposta (as rotas não importam `zod` — ADR-009).
export const Model3dListEnvelopeSchema = z.object({
  data: z.array(Model3dSummarySchema),
  error: z.null(),
});
export const Model3dDetailEnvelopeSchema = z.object({
  data: Model3dDetailSchema,
  error: z.null(),
});
